import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Clock, MapPin, Users, Send, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';

interface Game {
  id: string;
  title: string;
  date: string;
  time: string;
  status: string;
  needs_scorekeeper: boolean;
}

interface ScorekeeperApplication {
  id: string;
  game_id: string;
  game_title: string;
  game_date: string;
  game_time: string;
  message?: string;
  status: 'pending' | 'approved' | 'rejected';
  applied_at: string;
  rejection_reason?: string;
}

export const ScorekeeperApplicationSystem: React.FC = () => {
  const [availableGames, setAvailableGames] = useState<Game[]>([]);
  const [myApplications, setMyApplications] = useState<ScorekeeperApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuthenticatedUser();

  useEffect(() => {
    if (user?.id) {
      loadAvailableGames();
      loadMyApplications();
    }
  }, [user]);

  const loadAvailableGames = async () => {
    if (!user) return;
    
    try {
      // Load venue games that need scorekeepers
      const { data: venueGames, error: venueError } = await supabase
        .from('venue_games')
        .select('id, game_title, game_date, game_time, venue_id')
        .eq('scorekeeper_needed', true)
        .is('primary_scorekeeper_id', null)
        .gte('game_date', new Date().toISOString().split('T')[0])
        .order('game_date', { ascending: true });

      if (venueError) throw venueError;

      // Load regular games that need scorekeepers
      const { data: regularGames, error: regularError } = await supabase
        .from('games')
        .select('id, game_name, game_date, game_time, created_by')
        .eq('scorekeeper_needed', true)
        .is('primary_scorekeeper_id', null)
        .gte('game_date', new Date().toISOString().split('T')[0])
        .order('game_date', { ascending: true });

      if (regularError) throw regularError;

      // Filter out games the user has already applied to
      const { data: existingApplications } = await supabase
        .from('scorekeeper_applications')
        .select('game_id')
        .eq('scorekeeper_id', user.id);

      const appliedGameIds = new Set(existingApplications?.map(app => app.game_id) || []);

      const allGames = [
        ...(venueGames?.map(g => ({
          id: g.id,
          title: g.game_title,
          date: g.game_date,
          time: g.game_time,
          status: 'pending' as const,
          needs_scorekeeper: true
        })) || []),
        ...(regularGames?.map(g => ({
          id: g.id,
          title: g.game_name,
          date: g.game_date,
          time: g.game_time,
          status: 'pending' as const,
          needs_scorekeeper: true
        })) || [])
      ].filter(game => !appliedGameIds.has(game.id));

      setAvailableGames(allGames);
    } catch (error) {
      console.error('Error loading available games:', error);
      toast.error('Failed to load available games');
      setAvailableGames([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMyApplications = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('scorekeeper_applications')
        .select(`
          *,
          game:game_id (
            id,
            game_name,
            game_date,
            game_time
          ),
          venue_game:game_id (
            id,
            game_title,
            game_date,
            game_time
          )
        `)
        .eq('scorekeeper_id', user.id)
        .order('applied_at', { ascending: false });

      if (error) throw error;

      const formattedApplications = data?.map((app: any) => ({
        id: app.id,
        game_id: app.game_id,
        game_title: app.game?.game_name || app.venue_game?.game_title || 'Unknown Game',
        game_date: app.game?.game_date || app.venue_game?.game_date,
        game_time: app.game?.game_time || app.venue_game?.game_time,
        message: app.message,
        status: app.status,
        applied_at: app.applied_at,
        rejection_reason: app.rejection_reason
      })) || [];

      setMyApplications(formattedApplications);
    } catch (error) {
      console.error('Error loading applications:', error);
      toast.error('Failed to load your applications');
    }
  };

  const submitApplication = async () => {
    if (!user || !selectedGame) {
      toast.error('Please select a game');
      return;
    }

    setSubmitting(true);

    try {
      // Calculate backup position
      const { data: existingApps } = await supabase
        .from('scorekeeper_applications')
        .select('id')
        .eq('game_id', selectedGame);

      const backupPosition = (existingApps?.length || 0) + 1;

      // Submit application
      const { error } = await supabase
        .from('scorekeeper_applications')
        .insert({
          game_id: selectedGame,
          scorekeeper_id: user.id,
          message: applicationMessage || null,
          status: 'pending',
          backup_position: backupPosition,
          game_type: 'venue'
        });

      if (error) throw error;

      toast.success('Application submitted successfully!');
      setSelectedGame(null);
      setApplicationMessage('');
      loadAvailableGames();
      loadMyApplications();
    } catch (error: any) {
      console.error('Error submitting application:', error);
      toast.error(error?.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'default';
      case 'rejected': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="h-4 w-4" />;
      case 'rejected': return <XCircle className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="animate-pulse">
          <CardContent className="p-6">
            <div className="h-4 bg-muted rounded w-3/4 mb-4"></div>
            <div className="h-10 bg-muted rounded mb-4"></div>
            <div className="h-10 bg-muted rounded"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Application Form */}
      <Card>
        <CardHeader>
          <CardTitle>Apply for Scorekeeper Position</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium">Select Game</label>
            <Select value={selectedGame} onValueChange={setSelectedGame}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a game to apply for" />
              </SelectTrigger>
              <SelectContent>
                {availableGames.map((game) => (
                  <SelectItem key={game.id} value={game.id}>
                    {game.title} - {game.date} at {game.time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Message (Optional)</label>
            <Textarea
              placeholder="Tell the venue why you'd be a great scorekeeper for this game..."
              value={applicationMessage}
              onChange={(e) => setApplicationMessage(e.target.value)}
              rows={3}
            />
          </div>

          <Button 
            onClick={submitApplication}
            disabled={!selectedGame || submitting}
            className="w-full"
          >
            <Send className="h-4 w-4 mr-2" />
            Submit Application
          </Button>
        </CardContent>
      </Card>

      {/* Available Games */}
      <Card>
        <CardHeader>
          <CardTitle>Available Games ({availableGames.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {availableGames.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No games currently need scorekeepers</p>
            </div>
          ) : (
            <div className="space-y-3">
              {availableGames.map((game) => (
                <div key={game.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <h4 className="font-medium">{game.title}</h4>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {game.date}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {game.time}
                      </div>
                    </div>
                  </div>
                  <Badge variant="outline">Needs Scorekeeper</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Applications */}
      <Card>
        <CardHeader>
          <CardTitle>My Applications ({myApplications.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {myApplications.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Send className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>You haven't applied for any games yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myApplications.map((application) => (
                <div key={application.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium">{application.game_title}</h4>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Applied: {new Date(application.applied_at).toLocaleDateString()}</span>
                      {application.game_date && (
                        <>
                          <span>•</span>
                          <span>Game: {application.game_date} at {application.game_time}</span>
                        </>
                      )}
                    </div>
                    {application.message && (
                      <p className="text-sm text-muted-foreground mt-1 italic">
                        "{application.message}"
                      </p>
                    )}
                    {application.status === 'rejected' && application.rejection_reason && (
                      <p className="text-sm text-destructive mt-1">
                        Reason: {application.rejection_reason}
                      </p>
                    )}
                  </div>
                  <Badge variant={getStatusColor(application.status)}>
                    <span className="flex items-center gap-1">
                      {getStatusIcon(application.status)}
                      {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                    </span>
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
